import { db } from '../../firebase/firebaseConfig';
import { doc, setDoc, getDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { createReservation, updateReservation } from '../business/reservationService';
import { createTransporter } from '../firestore/transporterService';
import { createVehicle } from '../firestore/vehicleService';
import {
    logFacilityEntry,
    logDockArrival,
    logLoadingStart,
    logLoadingEnd,
    logFacilityExit
} from '../logging/vehicleMovementLogger';
import { COLLECTIONS } from '../../constants/collections';
import { VEHICLE_STATUS } from '../../constants/statuses';

// Fake User Context
const TEST_USER = {
    uid: 'test_system_admin',
    tenantId: 'tenant_demo_1', // Using a fixed demo tenant
    email: 'test@system.com'
};

const logger = (msg, addLog) => {
    console.log(`[TEST]: ${msg}`);
    addLog({ time: new Date().toLocaleTimeString(), msg, type: 'info' });
};

const errorLogger = (msg, err, addLog) => {
    console.error(`[TEST ERROR]: ${msg}`, err);
    addLog({ time: new Date().toLocaleTimeString(), msg: `${msg} - ${err.message}`, type: 'error' });
};

export const runFullTestScenario = async (addLog) => {
    try {
        logger('Starting Full System Test...', addLog);

        // 1. Setup Tenant & Context
        const tenantId = TEST_USER.tenantId;
        logger(`Using Tenant: ${tenantId}`, addLog);

        // Ensure Tenant Exists (Mock)
        // In real app we might not need this if security rules allow writes to this tenant path
        // but let's assume valid tenant.

        // 2. Create Master Data: Transporter
        logger('Creating/Checking Transporter...', addLog);
        // We'll just create a new one to avoid conflicts or complex lookups
        const transporterData = {
            name: `Test Lojistik ${Date.now()}`,
            tenantId: tenantId,
            contactPerson: 'Test Abi',
            email: `test${Date.now()}@lojistik.com`,
            phone: '5551234567',
            transporterType: 'standard'
        };
        // Note: createTransporter might expect just data. Let's check signature if possible. 
        // Assuming createTransporter(data)
        const transporterRef = await addDoc(collection(db, COLLECTIONS.TRANSPORTERS), {
            ...transporterData,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        const transporterId = transporterRef.id;
        logger(`Transporter Created: ${transporterId}`, addLog);

        // 3. Create Vehicle
        logger('Creating Vehicle...', addLog);
        const plate = `34TST${Math.floor(Math.random() * 900) + 100}`;
        const vehicleData = {
            licensePlate: plate,
            tenantId: tenantId,
            transporterId: transporterId,
            vehicleTypeId: 'vtype_truck_1360', // Assuming this exists or is string
            status: VEHICLE_STATUS.PENDING_APPROVAL,
            driver: { name: 'Test Driver', phone: '5559998877' }
        };
        const vehicleRef = await createVehicle(vehicleData);
        // createVehicle returns void or ref? Checking service... assuming it returns ref or we catch error
        // Actually the service usually returns docRef or similar.
        logger(`Vehicle Created: ${plate}`, addLog);

        // 4. Create Reservation (Coordinator Plan)
        logger('Creating Reservation...', addLog);
        const now = new Date();
        const startTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour later
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);

        const reservationData = {
            tenantId: tenantId,
            transporterId: transporterId,
            transporterName: transporterData.name,
            vehicleId: mapVehicleRefToId(vehicleRef), // Need to handle if createVehicle returns object or id
            plateNumber: plate,
            vehicleType: 'Tır',
            dockId: 'dock_test_A1',
            dockName: 'A1 Rampa',
            startTime: startTime,
            endTime: endTime,
            status: VEHICLE_STATUS.PENDING_APPROVAL // Initial state
        };

        // Note createReservation(data, performedBy)
        const reservationId = await createReservation(reservationData, TEST_USER.uid);
        logger(`Reservation Created: ${reservationId}`, addLog);

        // 5. Transporter Confirmation
        logger('Transporter Confirming...', addLog);
        await updateReservation(reservationId, { status: VEHICLE_STATUS.APPROVED }, TEST_USER.uid);
        logger('Reservation Confirmed.', addLog);

        // 6. Gate Entry (Check-In)
        logger('Gate Entry Process...', addLog);
        // a. Log Entry
        await logFacilityEntry(reservationId, plate, tenantId, TEST_USER.uid);
        // b. Update Status
        await updateReservation(reservationId, { status: VEHICLE_STATUS.FACILITY_ENTERED }, TEST_USER.uid);
        logger('Vehicle Checked In (Gate Cross).', addLog);

        // 7. Dock Arrival & Loading Start
        logger('Dock Process: Loading Start...', addLog);
        // We assume we know the logId? 
        // usage: logDockArrival(logId, dockName, performedBy)
        // We need to fetch the active log first

        // Dynamically import to avoid cyclic deps if any
        const { getVehicleMovementLogByReservation } = await import('../logging/vehicleMovementLogger');
        const activeLog = await getVehicleMovementLogByReservation(reservationId);
        if (!activeLog) throw new Error('Movement log not found after entry!');

        await logDockArrival(activeLog.id, 'A1 Rampa', TEST_USER.uid);
        await logLoadingStart(activeLog.id, 'A1 Rampa', TEST_USER.uid);
        await updateReservation(reservationId, { status: VEHICLE_STATUS.LOADING_STARTED }, TEST_USER.uid);
        logger('Loading Started.', addLog);

        // 8. Loading End
        logger('Dock Process: Loading End...', addLog);
        await logLoadingEnd(activeLog.id, 'A1 Rampa', TEST_USER.uid);
        await updateReservation(reservationId, { status: VEHICLE_STATUS.LOADING_COMPLETED }, TEST_USER.uid);
        logger('Loading Completed.', addLog);

        // 9. Gate Exit
        logger('Gate Exit Process...', addLog);
        await logFacilityExit(activeLog.id, TEST_USER.uid);
        await updateReservation(reservationId, { status: VEHICLE_STATUS.FACILITY_EXITED }, TEST_USER.uid);
        logger('Vehicle Exted (Completed).', addLog);

        logger('TEST SCENARIO COMPLETED SUCCESSFULLY ✅', addLog);
        return true;

    } catch (err) {
        errorLogger('Test Failed', err, addLog);
        return false;
    }
};

// Helper
const mapVehicleRefToId = (res) => {
    // If createVehicle returns string ID
    if (typeof res === 'string') return res;
    // If it returns a DocRef object
    if (res?.id) return res.id;
    return 'unknown_vehicle_id';
};
